import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';
import { personService } from '@shared/services/personService';

export const useLanguageForm = (person) => {
    const { t } = useTranslation();
    const [languageData, setLanguageData] = useState([]);
    const [languageOptions, setLanguageOptions] = useState([]);
    const [skillGrades, setSkillGrades] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingLanguage, setEditingLanguage] = useState(null);

    const languageValidationSchema = yup.object({
        language: yup.string()
            .required(t('validation.person.language.languageRequired'))
    });

    const {
        register: languageFormRegister,
        formState: { errors: languageErrors },
        handleSubmit,
        reset,
        setValue
    } = useForm({
        resolver: yupResolver(languageValidationSchema),
        defaultValues: {
            language: '',
            listening: '',
            reading: '',
            writing: '',
            spokenInteract: '',
            spokenProduction: ''
        }
    });

    const fetchPersonLanguages = async () => {
        try {
            if (person?.id) {
                const languages = await personService.getPersonLanguages(person.id);
                const transformedData = languages.map(lang => ({
                    id: lang.id,
                    language: lang.id.language,
                    listening: lang.listening,
                    reading: lang.reading,
                    writing: lang.writing,
                    spokenInteract: lang.spokenInteract,
                    spokenProduction: lang.spokenProduction
                }));
                setLanguageData(transformedData);
            }
        } catch (error) {
            console.error('Error fetching person languages:', error);
            notifyError(t('persons.language.fetchError'));
        }
    };

    const fetchLanguages = async () => {
        try {
            const languages = await personService.getLanguageList();
            setLanguageOptions(languages);
        } catch (error) {
            console.error('Error fetching languages:', error);
        }
    };

    const fetchSkillGrades = async () => {
        try {
            const grades = await personService.getLangSkillsGrades();
            setSkillGrades(grades);
        } catch (error) {
            console.error('Error fetching skill grades:', error);
        }
    };

    useEffect(() => {
        fetchLanguages();
        fetchSkillGrades();
        if (person?.id) {
            fetchPersonLanguages();
        }
    }, [person?.id]);

    const handleEdit = (language) => {
        setIsEditing(true);
        setEditingLanguage(language);
        setValue('language', language.language);
        setValue('listening', language.listening);
        setValue('reading', language.reading);
        setValue('writing', language.writing);
        setValue('spokenInteract', language.spokenInteract);
        setValue('spokenProduction', language.spokenProduction);
    };

    const submitLanguageForm = async (formData) => {
        try {
            if (!person?.id) {
                notifyError(t('persons.language.personIdRequired'));
                return false;
            }

            const payload = {
                personId: person.id,
                language: formData.language,
                writing: formData.writing,
                listening: formData.listening,
                spokenInteract: formData.spokenInteract,
                spokenProduction: formData.spokenProduction,
                reading: formData.reading
            };

            if (isEditing && editingLanguage) {
                await personService.updatePersonLanguage(person.id, editingLanguage.language, payload);
                notifySuccess(t('persons.language.updateSuccess'));
            } else {
                await personService.addPersonLanguage(payload);
                notifySuccess(t('persons.language.addSuccess'));
            }

            reset();
            setIsEditing(false);
            setEditingLanguage(null);
            await fetchPersonLanguages();
            return true;
        } catch (error) {
            console.error('Language operation failed:', error);
            notifyError(t('persons.language.addError'));
            return false;
        }
    };

    const handleDelete = async (languageId) => {
        try {
            if (!person?.id) {
                notifyError(t('persons.language.personIdRequired'));
                return;
            }
            
            await personService.deletePersonLanguage(person.id, languageId.language);
            notifySuccess(t('persons.language.deleteSuccess'));
            await fetchPersonLanguages(); // Refresh the list
        } catch (error) {
            console.error('Error deleting language:', error);
            notifyError(t('persons.language.deleteError'));
        }
    };

    return {
        languageData,
        languageFormRegister,
        languageErrors,
        handleDelete,
        submitLanguageForm,
        handleSubmit,
        languageOptions,
        skillGrades,
        handleEdit,
        isEditing
    };
}; 